// Lightweight, dependency-free i18n for the app UI.
//
// - Locale comes from the URL path prefix the build emits (/de/, /nl/, ...) when
//   there is one, otherwise it's auto-detected from the browser
//   (navigator.languages), matched by primary subtag against the supported set,
//   falling back to English. The path wins so a visitor arriving on an indexed
//   localized URL sees the language that URL claims to be.
// - Static markup is translated by tagging elements with data-i18n* attributes
//   and calling applyDom(); dynamic strings use t()/tCount() at runtime.
// - Plurals use Intl.PluralRules so each locale picks the right form.
//
// A ?lang=xx query param overrides detection (handy for testing without
// changing OS/browser settings). There is intentionally no in-app switcher.

export const SUPPORTED = ['en', 'es', 'fr', 'de', 'pt', 'nl'];
export const DEFAULT_LOCALE = 'en';

// Flat, dotted keys. English is the canonical set; other locales fall back to
// the English string for any key they're missing.
// Exported so the build can pre-translate the static markup for each locale's
// page (see build-seo.mjs) instead of shipping English and swapping it in JS.
export const messages = {
  en: {
    'common.copyLink': 'Copy link',
    'common.reset': 'Reset',
    'common.back': 'Back',
    'common.retry': 'Retry',
    'common.copied': 'Copied!',
    'common.gotIt': 'Got it',
    'common.close': 'Close',
    'resume.button': 'Resume watching',

    'role.host': 'Baby monitor',
    'role.parent': 'Parent',
    'safety.badge': 'Safe & secure.',
    'safety.why': 'Why?',
    'intro.short': 'Choose what this device becomes: baby monitor beside your child, or parent screen to watch from.',
    'intro.medium': 'A baby monitor in your browser, with no app or account. Pick Baby monitor for the device beside your child, or Parent to watch from. Scan the QR code to link them.',
    'intro.long.title': 'How Peekababy works',
    'intro.long.p1': 'Open peekababy.app on two devices. Choose Baby monitor beside your child, and Parent on the device you watch from.',
    'intro.long.p2': 'Scan the QR code to link them. No app or account, and the connection is encrypted.',

    'connect.status': 'This phone is now your baby monitor. Scan the QR code on another device to watch.',
    'hostStatus.waiting': 'Waiting for a parent…',
    'hostStatus.count.one': '{count} parent watching',
    'hostStatus.count.other': '{count} parents watching',

    'scan.hint': 'Point your camera at the QR code shown on the baby monitor.',
    'scan.openCamera': 'Open camera',
    'scan.scanning': 'Scanning…',
    'scan.cameraNeeded': 'Camera access is needed to scan the code.',

    'status.connecting': 'Connecting…',
    'status.connected': 'Connected',
    'status.live': 'Live',
    'status.reconnecting': 'Reconnecting…',
    'status.notFound': 'Can’t reach this monitor. Check the link or scan again.',
    'reconnect.title': 'Reconnecting…',
    'reconnect.hint': 'Connection interrupted. Restoring the live view.',
    'status.cameraStopped': 'Camera stopped',
    'cameraFrozen.title': 'Monitor camera stopped',
    'cameraFrozen.hint': 'The picture is frozen, but the connection is fine. Waiting for the camera to resume.',

    'sound.quiet': 'Quiet',
    'sound.none': 'No sound yet',
    'sound.level.low': 'Low',
    'sound.level.medium': 'Medium',
    'sound.level.loud': 'Loud',
    'sound.past.low': 'Was low',
    'sound.past.medium': 'Was medium',
    'sound.past.loud': 'Was loud',
    'sound.ago': '{time} ago',

    'install.text': 'Works better when installed',
    'install.action': 'Install',
    'install.dismiss': 'Dismiss',
    'install.iosHint': 'Install: tap Share, then “Add to Home Screen”',

    'update.available': 'A new version is available',
    'update.action': 'Refresh',

    'toolbar.switch': 'Switch',
    'toolbar.switchAria': 'Switch camera',
    'toolbar.screen': 'Screen',
    'toolbar.screenAria': 'Dim screen to save battery',
    'toolbar.addParent': 'Add parent',
    'toolbar.addParentAria': 'Add another parent',
    'toolbar.melody': 'Melody',
    'toolbar.melodyAria': 'Play a soothing melody for your baby',
    'melody.playing': 'Lullaby playing',
    'melody.alertsPaused': 'Cry alerts paused',
    'battery.low': 'Monitor battery low',
    'battery.level': '{level}% left',
    'toolbar.sound': 'Sound',
    'toolbar.soundAria': 'Toggle sound',
    'toolbar.talk': 'Talk',
    'toolbar.talkAria': 'Hold to talk',
    'toolbar.alerts': 'Alerts',
    'toolbar.alertsAria': 'Sound alerts when the app is closed',
    'toolbar.invite': 'Invite',
    'toolbar.inviteAria': 'Invite another parent',
    'toolbar.fill': 'Fill',
    'toolbar.fit': 'Fit',
    'toolbar.frameAria': 'Fit or fill the screen',

    'safety.title': 'Private & secure by design',
    'safety.item1': 'Video and audio travel <strong>directly</strong> between your two devices: peer-to-peer, encrypted end to end (WebRTC / DTLS-SRTP).',
    'safety.item2': "No account, no sign-up, nothing stored on a server. There's no recording anywhere.",
    'safety.item3': 'The connection link is a random one-time id: only a device that scans your QR can watch.',
    'safety.author': 'Built by Harm Jan L. →',
    'safety.melodyCredit': 'Melody by <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> from <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Invite another parent',
    'invite.sub': 'Let someone else watch too: they scan this code or open the link.',

    'videoOff.status': 'Streaming, screen dimmed to save battery',
    'videoOff.hint': 'Tap anywhere to show the picture',

    'error.camera.denied': 'Allow camera & microphone access in your browser settings, then try again.',
    'error.camera.busy': 'Your camera is in use by another app. Close it and try again.',
    'error.camera.notFound': 'No camera found. Connect one and try again.',
    'error.camera.generic': "Couldn't start your camera. Please try again.",
    'error.connection': 'Connection failed. Check your internet and try again.',

    'alerts.enabled': "Alerts on. You'll be notified even when the app is closed.",
    'alerts.disabled': 'Alerts off.',
    'alerts.denied': 'Notifications are blocked. Allow them in your browser settings to get alerts.',
    'alerts.unsupported': "This browser can't send background alerts.",
    'alerts.unsupportedIos': 'Add the app to your Home Screen first to get background alerts.',
    'alerts.failed': "Couldn't turn on alerts. Check your connection and try again.",

    'trust.defaultName': 'Parent-{suffix}',
    'trust.newParent': 'A new parent connected',
    'trust.newParentNamed': '{name} connected',
    'trust.allow': 'Always allow',
    'trust.ignore': 'Ignore',
    'trust.manage': 'Trusted parents',
    'trust.title': 'Trusted parents',
    'trust.sub': 'Devices this monitor recognizes connect without a prompt. Remove any you no longer trust.',
    'trust.empty': 'No trusted parents yet.',
    'trust.remove': 'Remove',
    'trust.nameAria': 'Parent name',
  },

  es: {
    'common.copyLink': 'Copiar enlace',
    'common.reset': 'Reiniciar',
    'common.back': 'Atrás',
    'common.retry': 'Reintentar',
    'common.copied': '¡Copiado!',
    'common.gotIt': 'Entendido',
    'common.close': 'Cerrar',
    'resume.button': 'Seguir viendo',

    'role.host': 'Monitor de bebé',
    'role.parent': 'Padres',
    'safety.badge': 'Seguro y privado.',
    'safety.why': '¿Por qué?',
    'intro.short': 'Elige en qué se convierte este dispositivo: monitor junto a tu bebé, o pantalla de padres para vigilar.',
    'intro.medium': 'Un monitor de bebé en tu navegador, sin app ni cuenta. Elige Monitor de bebé para el dispositivo junto a tu bebé, o Padres para vigilar. Escanea el código QR para vincularlos.',
    'intro.long.title': 'Cómo funciona Peekababy',
    'intro.long.p1': 'Abre peekababy.app en dos dispositivos. Elige Monitor de bebé junto a tu bebé y Padres en el dispositivo desde el que vigilas.',
    'intro.long.p2': 'Escanea el código QR para vincularlos. Sin app ni cuenta, y la conexión está cifrada.',

    'connect.status': 'Este teléfono es ahora tu monitor de bebé. Escanea el código QR con otro dispositivo para verlo.',
    'hostStatus.waiting': 'Esperando a un padre…',
    'hostStatus.count.one': '{count} padre viendo',
    'hostStatus.count.other': '{count} padres viendo',

    'scan.hint': 'Apunta la cámara al código QR que aparece en el monitor de bebé.',
    'scan.openCamera': 'Abrir cámara',
    'scan.scanning': 'Escaneando…',
    'scan.cameraNeeded': 'Se necesita acceso a la cámara para escanear el código.',

    'status.connecting': 'Conectando…',
    'status.connected': 'Conectado',
    'status.live': 'En directo',
    'status.reconnecting': 'Reconectando…',
    'status.notFound': 'No se puede conectar con el monitor. Revisa el enlace o vuelve a escanear.',
    'reconnect.title': 'Reconectando…',
    'reconnect.hint': 'Conexión interrumpida: restaurando la vista en directo.',
    'status.cameraStopped': 'Cámara detenida',
    'cameraFrozen.title': 'La cámara del monitor se detuvo',
    'cameraFrozen.hint': 'La imagen está congelada, pero la conexión funciona. Esperando a que la cámara se reanude.',

    'sound.quiet': 'Silencio',
    'sound.none': 'Aún sin sonido',
    'sound.level.low': 'Bajo',
    'sound.level.medium': 'Medio',
    'sound.level.loud': 'Alto',
    'sound.past.low': 'Fue bajo',
    'sound.past.medium': 'Fue medio',
    'sound.past.loud': 'Fue alto',
    'sound.ago': 'hace {time}',

    'install.text': 'Funciona mejor instalada',
    'install.action': 'Instalar',
    'install.dismiss': 'Descartar',
    'install.iosHint': 'Instalar: toca Compartir y luego “Añadir a pantalla de inicio”',

    'update.available': 'Hay una nueva versión disponible',
    'update.action': 'Actualizar',

    'toolbar.switch': 'Cambiar',
    'toolbar.switchAria': 'Cambiar cámara',
    'toolbar.screen': 'Pantalla',
    'toolbar.screenAria': 'Atenuar pantalla para ahorrar batería',
    'toolbar.addParent': 'Añadir padre',
    'toolbar.addParentAria': 'Añadir otro padre',
    'toolbar.melody': 'Melodía',
    'toolbar.melodyAria': 'Reproducir una melodía relajante para tu bebé',
    'melody.playing': 'Sonando nana',
    'melody.alertsPaused': 'Alertas de llanto en pausa',
    'battery.low': 'Batería del monitor baja',
    'battery.level': 'Queda {level}%',
    'toolbar.sound': 'Sonido',
    'toolbar.soundAria': 'Activar o silenciar el sonido',
    'toolbar.talk': 'Hablar',
    'toolbar.talkAria': 'Mantén pulsado para hablar',
    'toolbar.alerts': 'Alertas',
    'toolbar.alertsAria': 'Alertas sonoras cuando la app está cerrada',
    'toolbar.invite': 'Invitar',
    'toolbar.inviteAria': 'Invitar a otro padre',
    'toolbar.fill': 'Llenar',
    'toolbar.fit': 'Ajustar',
    'toolbar.frameAria': 'Ajustar o llenar la pantalla',

    'safety.title': 'Privado y seguro por diseño',
    'safety.item1': 'El vídeo y el audio viajan <strong>directamente</strong> entre tus dos dispositivos: de igual a igual, cifrados de extremo a extremo (WebRTC / DTLS-SRTP).',
    'safety.item2': 'Sin cuenta, sin registro, nada se guarda en un servidor. No se graba en ningún sitio.',
    'safety.item3': 'El enlace de conexión es un id único y aleatorio: solo un dispositivo que escanee tu QR puede ver.',
    'safety.author': 'Creado por Harm Jan L. →',
    'safety.melodyCredit': 'Melodía de <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> en <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Invitar a otro padre',
    'invite.sub': 'Deja que otra persona también mire: escanea este código o abre el enlace.',

    'videoOff.status': 'Transmitiendo: pantalla atenuada para ahorrar batería',
    'videoOff.hint': 'Toca en cualquier parte para mostrar la imagen',

    'error.camera.denied': 'Permite el acceso a la cámara y el micrófono en los ajustes del navegador e inténtalo de nuevo.',
    'error.camera.busy': 'Otra app está usando la cámara. Ciérrala e inténtalo de nuevo.',
    'error.camera.notFound': 'No se encontró ninguna cámara. Conecta una e inténtalo de nuevo.',
    'error.camera.generic': 'No se pudo iniciar la cámara. Inténtalo de nuevo.',
    'error.connection': 'Fallo de conexión. Revisa tu conexión a internet e inténtalo de nuevo.',

    'alerts.enabled': 'Alertas activadas: te avisaremos aunque la app esté cerrada.',
    'alerts.disabled': 'Alertas desactivadas.',
    'alerts.denied': 'Las notificaciones están bloqueadas. Actívalas en los ajustes del navegador.',
    'alerts.unsupported': 'Este navegador no admite alertas en segundo plano.',
    'alerts.unsupportedIos': 'Añade la app a la pantalla de inicio para recibir alertas.',
    'alerts.failed': 'No se pudieron activar las alertas. Revisa tu conexión e inténtalo de nuevo.',

    'trust.defaultName': 'Padre-{suffix}',
    'trust.newParent': 'Se ha conectado un nuevo padre',
    'trust.newParentNamed': '{name} se ha conectado',
    'trust.allow': 'Permitir siempre',
    'trust.ignore': 'Ignorar',
    'trust.manage': 'Padres de confianza',
    'trust.title': 'Padres de confianza',
    'trust.sub': 'Los dispositivos que este monitor reconoce se conectan sin aviso. Elimina los que ya no sean de confianza.',
    'trust.empty': 'Aún no hay padres de confianza.',
    'trust.remove': 'Eliminar',
    'trust.nameAria': 'Nombre del padre',
  },

  fr: {
    'common.copyLink': 'Copier le lien',
    'common.reset': 'Réinitialiser',
    'common.back': 'Retour',
    'common.retry': 'Réessayer',
    'common.copied': 'Copié !',
    'common.gotIt': 'Compris',
    'common.close': 'Fermer',
    'resume.button': 'Continuer à regarder',

    'role.host': 'Écoute-bébé',
    'role.parent': 'Parent',
    'safety.badge': 'Sûr et sécurisé.',
    'safety.why': 'Pourquoi ?',
    'intro.short': 'Choisissez ce que devient cet appareil : écoute-bébé près de votre enfant, ou écran parent pour surveiller.',
    'intro.medium': 'Un écoute-bébé dans votre navigateur, sans application ni compte. Choisissez Écoute-bébé pour l’appareil près de votre enfant, ou Parent pour surveiller. Scannez le code QR pour les relier.',
    'intro.long.title': 'Comment fonctionne Peekababy',
    'intro.long.p1': 'Ouvrez peekababy.app sur deux appareils. Choisissez Écoute-bébé près de votre enfant et Parent sur l’appareil depuis lequel vous surveillez.',
    'intro.long.p2': 'Scannez le code QR pour les relier. Sans application ni compte, et la connexion est chiffrée.',

    'connect.status': 'Ce téléphone est maintenant votre écoute-bébé. Scannez le code QR avec un autre appareil pour regarder.',
    'hostStatus.waiting': 'En attente d’un parent…',
    'hostStatus.count.one': '{count} parent connecté',
    'hostStatus.count.other': '{count} parents connectés',

    'scan.hint': 'Dirigez votre caméra vers le code QR affiché sur l’écoute-bébé.',
    'scan.openCamera': 'Ouvrir la caméra',
    'scan.scanning': 'Analyse…',
    'scan.cameraNeeded': 'L’accès à la caméra est nécessaire pour scanner le code.',

    'status.connecting': 'Connexion…',
    'status.connected': 'Connecté',
    'status.live': 'En direct',
    'status.reconnecting': 'Reconnexion…',
    'status.notFound': 'Moniteur injoignable. Vérifiez le lien ou rescannez.',
    'reconnect.title': 'Reconnexion…',
    'reconnect.hint': 'Connexion interrompue. Rétablissement de la vue en direct.',
    'status.cameraStopped': 'Caméra arrêtée',
    'cameraFrozen.title': 'La caméra du moniteur s’est arrêtée',
    'cameraFrozen.hint': 'L’image est figée, mais la connexion fonctionne. En attente de la reprise de la caméra.',

    'sound.quiet': 'Silence',
    'sound.none': 'Pas encore de son',
    'sound.level.low': 'Faible',
    'sound.level.medium': 'Moyen',
    'sound.level.loud': 'Fort',
    'sound.past.low': 'Était faible',
    'sound.past.medium': 'Était moyen',
    'sound.past.loud': 'Était fort',
    'sound.ago': 'il y a {time}',

    'install.text': 'Fonctionne mieux une fois installée',
    'install.action': 'Installer',
    'install.dismiss': 'Fermer',
    'install.iosHint': 'Installer : appuyez sur Partager, puis “Sur l’écran d’accueil”',

    'update.available': 'Une nouvelle version est disponible',
    'update.action': 'Actualiser',

    'toolbar.switch': 'Changer',
    'toolbar.switchAria': 'Changer de caméra',
    'toolbar.screen': 'Écran',
    'toolbar.screenAria': 'Réduire l’écran pour économiser la batterie',
    'toolbar.addParent': 'Ajouter un parent',
    'toolbar.addParentAria': 'Ajouter un autre parent',
    'toolbar.melody': 'Mélodie',
    'toolbar.melodyAria': 'Jouer une mélodie apaisante pour votre bébé',
    'melody.playing': 'Berceuse en cours',
    'melody.alertsPaused': 'Alertes de pleurs en pause',
    'battery.low': 'Batterie de l’écoute-bébé faible',
    'battery.level': '{level}% restants',
    'toolbar.sound': 'Son',
    'toolbar.soundAria': 'Activer ou couper le son',
    'toolbar.talk': 'Parler',
    'toolbar.talkAria': 'Maintenir pour parler',
    'toolbar.alerts': 'Alertes',
    'toolbar.alertsAria': 'Alertes sonores quand l’appli est fermée',
    'toolbar.invite': 'Inviter',
    'toolbar.inviteAria': 'Inviter un autre parent',
    'toolbar.fill': 'Remplir',
    'toolbar.fit': 'Ajuster',
    'toolbar.frameAria': 'Ajuster ou remplir l’écran',

    'safety.title': 'Privé et sécurisé par conception',
    'safety.item1': 'La vidéo et l’audio circulent <strong>directement</strong> entre vos deux appareils : pair à pair, chiffrés de bout en bout (WebRTC / DTLS-SRTP).',
    'safety.item2': 'Pas de compte, pas d’inscription, rien n’est stocké sur un serveur. Aucun enregistrement nulle part.',
    'safety.item3': 'Le lien de connexion est un identifiant unique et aléatoire : seul un appareil qui scanne votre QR peut regarder.',
    'safety.author': 'Conçu par Harm Jan L. →',
    'safety.melodyCredit': 'Mélodie par <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> sur <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Inviter un autre parent',
    'invite.sub': 'Laissez quelqu’un d’autre regarder aussi : il scanne ce code ou ouvre le lien.',

    'videoOff.status': 'Diffusion en cours, écran atténué pour économiser la batterie',
    'videoOff.hint': 'Touchez n’importe où pour afficher l’image',

    'error.camera.denied': "Autorisez l'accès à la caméra et au micro dans les réglages du navigateur, puis réessayez.",
    'error.camera.busy': 'Une autre appli utilise la caméra. Fermez-la et réessayez.',
    'error.camera.notFound': 'Aucune caméra détectée. Branchez-en une et réessayez.',
    'error.camera.generic': 'Impossible de démarrer la caméra. Réessayez.',
    'error.connection': 'Échec de la connexion. Vérifiez votre connexion internet et réessayez.',

    'alerts.enabled': 'Alertes activées : vous serez prévenu même quand l’appli est fermée.',
    'alerts.disabled': 'Alertes désactivées.',
    'alerts.denied': 'Les notifications sont bloquées. Autorisez-les dans les réglages du navigateur.',
    'alerts.unsupported': 'Ce navigateur ne prend pas en charge les alertes en arrière-plan.',
    'alerts.unsupportedIos': 'Ajoutez l’appli à l’écran d’accueil pour recevoir les alertes.',
    'alerts.failed': 'Impossible d’activer les alertes. Vérifiez votre connexion et réessayez.',

    'trust.defaultName': 'Parent-{suffix}',
    'trust.newParent': 'Un nouveau parent s’est connecté',
    'trust.newParentNamed': '{name} s’est connecté',
    'trust.allow': 'Toujours autoriser',
    'trust.ignore': 'Ignorer',
    'trust.manage': 'Parents de confiance',
    'trust.title': 'Parents de confiance',
    'trust.sub': 'Les appareils que ce moniteur reconnaît se connectent sans demande. Retirez ceux en qui vous n’avez plus confiance.',
    'trust.empty': 'Aucun parent de confiance pour l’instant.',
    'trust.remove': 'Retirer',
    'trust.nameAria': 'Nom du parent',
  },

  de: {
    'common.copyLink': 'Link kopieren',
    'common.reset': 'Zurücksetzen',
    'common.back': 'Zurück',
    'common.retry': 'Erneut versuchen',
    'common.copied': 'Kopiert!',
    'common.gotIt': 'Verstanden',
    'common.close': 'Schließen',
    'resume.button': 'Weiter ansehen',

    'role.host': 'Babyphone',
    'role.parent': 'Elternteil',
    'safety.badge': 'Sicher & geschützt.',
    'safety.why': 'Warum?',
    'intro.short': 'Wähle, was dieses Gerät wird: Babyphone bei deinem Kind oder Elternbildschirm zum Mitschauen.',
    'intro.medium': 'Ein Babyphone im Browser, ohne App und ohne Konto. Wähle Babyphone für das Gerät bei deinem Kind oder Elternteil zum Mitschauen. Scanne den QR-Code, um sie zu verbinden.',
    'intro.long.title': 'So funktioniert Peekababy',
    'intro.long.p1': 'Öffne peekababy.app auf zwei Geräten. Wähle Babyphone beim Kind und Elternteil auf dem Gerät, auf dem du mitschaust.',
    'intro.long.p2': 'Scanne den QR-Code, um sie zu verbinden. Ohne App und Konto, und die Verbindung ist verschlüsselt.',

    'connect.status': 'Dieses Telefon ist jetzt dein Babyphone. Scanne den QR-Code mit einem anderen Gerät, um zuzusehen.',
    'hostStatus.waiting': 'Warten auf ein Elternteil…',
    'hostStatus.count.one': '{count} Elternteil sieht zu',
    'hostStatus.count.other': '{count} Eltern sehen zu',

    'scan.hint': 'Richte die Kamera auf den QR-Code, der auf dem Babyphone angezeigt wird.',
    'scan.openCamera': 'Kamera öffnen',
    'scan.scanning': 'Scannen…',
    'scan.cameraNeeded': 'Für das Scannen des Codes wird Kamerazugriff benötigt.',

    'status.connecting': 'Verbinden…',
    'status.connected': 'Verbunden',
    'status.live': 'Live',
    'status.reconnecting': 'Neu verbinden…',
    'status.notFound': 'Babyphone nicht erreichbar. Link prüfen oder erneut scannen.',
    'reconnect.title': 'Neu verbinden…',
    'reconnect.hint': 'Verbindung unterbrochen. Live-Bild wird wiederhergestellt.',
    'status.cameraStopped': 'Kamera angehalten',
    'cameraFrozen.title': 'Kamera des Babyphones angehalten',
    'cameraFrozen.hint': 'Das Bild ist eingefroren, aber die Verbindung steht. Warten, bis die Kamera wieder läuft.',

    'sound.quiet': 'Ruhig',
    'sound.none': 'Noch kein Ton',
    'sound.level.low': 'Leise',
    'sound.level.medium': 'Mittel',
    'sound.level.loud': 'Laut',
    'sound.past.low': 'War leise',
    'sound.past.medium': 'War mittel',
    'sound.past.loud': 'War laut',
    'sound.ago': 'vor {time}',

    'install.text': 'Funktioniert besser als App',
    'install.action': 'Installieren',
    'install.dismiss': 'Schließen',
    'install.iosHint': 'Installieren: auf Teilen tippen, dann „Zum Home-Bildschirm“',

    'update.available': 'Eine neue Version ist verfügbar',
    'update.action': 'Aktualisieren',

    'toolbar.switch': 'Wechseln',
    'toolbar.switchAria': 'Kamera wechseln',
    'toolbar.screen': 'Bildschirm',
    'toolbar.screenAria': 'Bildschirm abdunkeln, um Akku zu sparen',
    'toolbar.addParent': 'Elternteil hinzufügen',
    'toolbar.addParentAria': 'Weiteres Elternteil hinzufügen',
    'toolbar.melody': 'Melodie',
    'toolbar.melodyAria': 'Eine beruhigende Melodie für dein Baby abspielen',
    'melody.playing': 'Schlaflied läuft',
    'melody.alertsPaused': 'Schrei-Warnungen pausiert',
    'battery.low': 'Akku des Babyphones niedrig',
    'battery.level': '{level}% übrig',
    'toolbar.sound': 'Ton',
    'toolbar.soundAria': 'Ton ein- oder ausschalten',
    'toolbar.talk': 'Sprechen',
    'toolbar.talkAria': 'Zum Sprechen halten',
    'toolbar.alerts': 'Warnungen',
    'toolbar.alertsAria': 'Tonhinweise, wenn die App geschlossen ist',
    'toolbar.invite': 'Einladen',
    'toolbar.inviteAria': 'Weiteres Elternteil einladen',
    'toolbar.fill': 'Füllen',
    'toolbar.fit': 'Anpassen',
    'toolbar.frameAria': 'Bild anpassen oder Bildschirm füllen',

    'safety.title': 'Privat & sicher konzipiert',
    'safety.item1': 'Video und Audio werden <strong>direkt</strong> zwischen deinen beiden Geräten übertragen: Peer-to-Peer, Ende-zu-Ende verschlüsselt (WebRTC / DTLS-SRTP).',
    'safety.item2': 'Kein Konto, keine Anmeldung, nichts wird auf einem Server gespeichert. Es gibt keine Aufzeichnung.',
    'safety.item3': 'Der Verbindungslink ist eine zufällige Einmal-ID: nur ein Gerät, das deinen QR-Code scannt, kann zusehen.',
    'safety.author': 'Entwickelt von Harm Jan L. →',
    'safety.melodyCredit': 'Melodie von <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> auf <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Weiteres Elternteil einladen',
    'invite.sub': 'Lass jemand anderen mitschauen: einfach diesen Code scannen oder den Link öffnen.',

    'videoOff.status': 'Übertragung läuft, Bildschirm abgedunkelt, um Akku zu sparen',
    'videoOff.hint': 'Tippe irgendwo, um das Bild anzuzeigen',

    'error.camera.denied': 'Erlaube den Kamera- und Mikrofonzugriff in den Browsereinstellungen und versuche es erneut.',
    'error.camera.busy': 'Eine andere App nutzt die Kamera. Schließe sie und versuche es erneut.',
    'error.camera.notFound': 'Keine Kamera gefunden. Schließe eine an und versuche es erneut.',
    'error.camera.generic': 'Kamera konnte nicht gestartet werden. Bitte versuche es erneut.',
    'error.connection': 'Verbindung fehlgeschlagen. Prüfe deine Internetverbindung und versuche es erneut.',

    'alerts.enabled': 'Warnungen aktiv. Du wirst benachrichtigt, auch wenn die App geschlossen ist.',
    'alerts.disabled': 'Warnungen aus.',
    'alerts.denied': 'Benachrichtigungen sind blockiert. Erlaube sie in den Browsereinstellungen.',
    'alerts.unsupported': 'Dieser Browser unterstützt keine Hintergrundwarnungen.',
    'alerts.unsupportedIos': 'Füge die App zum Home-Bildschirm hinzu, um Warnungen zu erhalten.',
    'alerts.failed': 'Warnungen konnten nicht aktiviert werden. Prüfe deine Verbindung und versuch es erneut.',

    'trust.defaultName': 'Elternteil-{suffix}',
    'trust.newParent': 'Ein neues Elternteil hat sich verbunden',
    'trust.newParentNamed': '{name} hat sich verbunden',
    'trust.allow': 'Immer erlauben',
    'trust.ignore': 'Ignorieren',
    'trust.manage': 'Vertraute Eltern',
    'trust.title': 'Vertraute Eltern',
    'trust.sub': 'Geräte, die dieses Babyphone erkennt, verbinden sich ohne Nachfrage. Entferne alle, denen du nicht mehr vertraust.',
    'trust.empty': 'Noch keine vertrauten Eltern.',
    'trust.remove': 'Entfernen',
    'trust.nameAria': 'Name des Elternteils',
  },

  pt: {
    'common.copyLink': 'Copiar link',
    'common.reset': 'Reiniciar',
    'common.back': 'Voltar',
    'common.retry': 'Tentar novamente',
    'common.copied': 'Copiado!',
    'common.gotIt': 'Entendi',
    'common.close': 'Fechar',
    'resume.button': 'Continuar assistindo',

    'role.host': 'Monitor de bebê',
    'role.parent': 'Pais',
    'safety.badge': 'Seguro e privado.',
    'safety.why': 'Porquê?',
    'intro.short': 'Escolha o que este aparelho será: monitor perto do seu bebê, ou tela dos pais para acompanhar.',
    'intro.medium': 'Um monitor de bebê no navegador, sem aplicativo nem conta. Escolha Monitor de bebê para o aparelho perto do seu bebê, ou Pais para acompanhar. Escaneie o código QR para conectá-los.',
    'intro.long.title': 'Como o Peekababy funciona',
    'intro.long.p1': 'Abra peekababy.app em dois aparelhos. Escolha Monitor de bebê perto do seu bebê e Pais no aparelho de onde você acompanha.',
    'intro.long.p2': 'Escaneie o código QR para conectá-los. Sem aplicativo nem conta, e a conexão é criptografada.',

    'connect.status': 'Este telefone agora é o seu monitor de bebê. Escaneie o código QR em outro dispositivo para assistir.',
    'hostStatus.waiting': 'Aguardando um pai/mãe…',
    'hostStatus.count.one': '{count} pai/mãe assistindo',
    'hostStatus.count.other': '{count} pais assistindo',

    'scan.hint': 'Aponte a câmera para o código QR exibido no monitor de bebê.',
    'scan.openCamera': 'Abrir câmera',
    'scan.scanning': 'Escaneando…',
    'scan.cameraNeeded': 'É necessário acesso à câmera para escanear o código.',

    'status.connecting': 'Conectando…',
    'status.connected': 'Conectado',
    'status.live': 'Ao vivo',
    'status.reconnecting': 'Reconectando…',
    'status.notFound': 'Não foi possível acessar o monitor. Verifique o link ou escaneie novamente.',
    'reconnect.title': 'Reconectando…',
    'reconnect.hint': 'Conexão interrompida. Restaurando a visualização ao vivo.',
    'status.cameraStopped': 'Câmera parada',
    'cameraFrozen.title': 'A câmera do monitor parou',
    'cameraFrozen.hint': 'A imagem está congelada, mas a conexão está boa. Aguardando a câmera voltar.',

    'sound.quiet': 'Silêncio',
    'sound.none': 'Ainda sem som',
    'sound.level.low': 'Baixo',
    'sound.level.medium': 'Médio',
    'sound.level.loud': 'Alto',
    'sound.past.low': 'Foi baixo',
    'sound.past.medium': 'Foi médio',
    'sound.past.loud': 'Foi alto',
    'sound.ago': 'há {time}',

    'install.text': 'Funciona melhor instalado',
    'install.action': 'Instalar',
    'install.dismiss': 'Dispensar',
    'install.iosHint': 'Instalar: toque em Compartilhar e depois “Adicionar à Tela de Início”',

    'update.available': 'Uma nova versão está disponível',
    'update.action': 'Atualizar',

    'toolbar.switch': 'Trocar',
    'toolbar.switchAria': 'Trocar câmera',
    'toolbar.screen': 'Tela',
    'toolbar.screenAria': 'Escurecer a tela para poupar bateria',
    'toolbar.addParent': 'Adicionar',
    'toolbar.addParentAria': 'Adicionar outro pai/mãe',
    'toolbar.melody': 'Melodia',
    'toolbar.melodyAria': 'Tocar uma melodia relaxante para o seu bebê',
    'melody.playing': 'Cantiga tocando',
    'melody.alertsPaused': 'Alertas de choro pausados',
    'battery.low': 'Bateria do monitor fraca',
    'battery.level': 'Resta {level}%',
    'toolbar.sound': 'Som',
    'toolbar.soundAria': 'Ativar ou desativar o som',
    'toolbar.talk': 'Falar',
    'toolbar.talkAria': 'Segure para falar',
    'toolbar.alerts': 'Alertas',
    'toolbar.alertsAria': 'Alertas sonoros quando o app está fechado',
    'toolbar.invite': 'Convidar',
    'toolbar.inviteAria': 'Convidar outro pai/mãe',
    'toolbar.fill': 'Preencher',
    'toolbar.fit': 'Ajustar',
    'toolbar.frameAria': 'Ajustar ou preencher a tela',

    'safety.title': 'Privado e seguro por design',
    'safety.item1': 'O vídeo e o áudio trafegam <strong>diretamente</strong> entre seus dois dispositivos: ponto a ponto, criptografados de ponta a ponta (WebRTC / DTLS-SRTP).',
    'safety.item2': 'Sem conta, sem cadastro, nada é armazenado em um servidor. Não há gravação em lugar nenhum.',
    'safety.item3': 'O link de conexão é um id único e aleatório: só um dispositivo que escanear seu QR pode assistir.',
    'safety.author': 'Criado por Harm Jan L. →',
    'safety.melodyCredit': 'Melodia de <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> no <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Convidar outro pai/mãe',
    'invite.sub': 'Deixe outra pessoa assistir também: basta escanear este código ou abrir o link.',

    'videoOff.status': 'Transmitindo, tela escurecida para poupar bateria',
    'videoOff.hint': 'Toque em qualquer lugar para mostrar a imagem',

    'error.camera.denied': 'Permita o acesso à câmera e ao microfone nas configurações do navegador e tente de novo.',
    'error.camera.busy': 'Outro app está usando a câmera. Feche-o e tente de novo.',
    'error.camera.notFound': 'Nenhuma câmera encontrada. Conecte uma e tente de novo.',
    'error.camera.generic': 'Não foi possível iniciar a câmera. Tente de novo.',
    'error.connection': 'Falha na conexão. Verifique sua internet e tente de novo.',

    'alerts.enabled': 'Alertas ativados: você será avisado mesmo com o app fechado.',
    'alerts.disabled': 'Alertas desativados.',
    'alerts.denied': 'As notificações estão bloqueadas. Ative-as nas configurações do navegador.',
    'alerts.unsupported': 'Este navegador não oferece alertas em segundo plano.',
    'alerts.unsupportedIos': 'Adicione o app à Tela de Início para receber alertas.',
    'alerts.failed': 'Não foi possível ativar os alertas. Verifique sua conexão e tente novamente.',

    'trust.defaultName': 'Responsável-{suffix}',
    'trust.newParent': 'Um novo responsável conectou-se',
    'trust.newParentNamed': '{name} conectou-se',
    'trust.allow': 'Permitir sempre',
    'trust.ignore': 'Ignorar',
    'trust.manage': 'Responsáveis de confiança',
    'trust.title': 'Responsáveis de confiança',
    'trust.sub': 'Os dispositivos que este monitor reconhece conectam-se sem aviso. Remova os que já não são de confiança.',
    'trust.empty': 'Ainda não há responsáveis de confiança.',
    'trust.remove': 'Remover',
    'trust.nameAria': 'Nome do responsável',
  },

  nl: {
    'common.copyLink': 'Link kopiëren',
    'common.reset': 'Opnieuw',
    'common.back': 'Terug',
    'common.retry': 'Opnieuw proberen',
    'common.copied': 'Gekopieerd!',
    'common.gotIt': 'Begrepen',
    'common.close': 'Sluiten',
    'resume.button': 'Verder kijken',

    'role.host': 'Babyfoon',
    'role.parent': 'Ouder',
    'safety.badge': 'Veilig & beschermd.',
    'safety.why': 'Waarom?',
    'intro.short': 'Kies wat dit apparaat wordt: babyfoon bij je kindje, of ouder-scherm om mee te kijken.',
    'intro.medium': 'Een babyfoon in je browser, zonder app of account. Kies Babyfoon voor het apparaat bij je kindje, of Ouder om mee te kijken. Scan de QR-code om ze te koppelen.',
    'intro.long.title': 'Zo werkt Peekababy',
    'intro.long.p1': 'Open peekababy.app op twee apparaten. Kies Babyfoon bij je kindje en Ouder op het apparaat waarop je meekijkt.',
    'intro.long.p2': 'Scan de QR-code om ze te koppelen. Zonder app of account, en de verbinding is versleuteld.',

    'connect.status': 'Deze telefoon is nu je babyfoon. Scan de QR-code op een ander apparaat om mee te kijken.',
    'hostStatus.waiting': 'Wachten op een ouder…',
    'hostStatus.count.one': '{count} ouder kijkt mee',
    'hostStatus.count.other': '{count} ouders kijken mee',

    'scan.hint': 'Richt je camera op de QR-code op de babyfoon.',
    'scan.openCamera': 'Camera openen',
    'scan.scanning': 'Scannen…',
    'scan.cameraNeeded': 'Cameratoegang is nodig om de code te scannen.',

    'status.connecting': 'Verbinden…',
    'status.connected': 'Verbonden',
    'status.live': 'Live',
    'status.reconnecting': 'Opnieuw verbinden…',
    'status.notFound': 'Kan de babyfoon niet bereiken. Controleer de link of scan opnieuw.',
    'reconnect.title': 'Opnieuw verbinden…',
    'reconnect.hint': 'Verbinding onderbroken. Livebeeld wordt hersteld.',
    'status.cameraStopped': 'Camera gestopt',
    'cameraFrozen.title': 'Camera van de babyfoon gestopt',
    'cameraFrozen.hint': 'Het beeld is bevroren, maar de verbinding is in orde. Wachten tot de camera hervat.',

    'sound.quiet': 'Stil',
    'sound.none': 'Nog geen geluid',
    'sound.level.low': 'Laag',
    'sound.level.medium': 'Gemiddeld',
    'sound.level.loud': 'Luid',
    'sound.past.low': 'Was laag',
    'sound.past.medium': 'Was gemiddeld',
    'sound.past.loud': 'Was luid',
    'sound.ago': '{time} geleden',

    'install.text': 'Werkt beter als app',
    'install.action': 'Installeren',
    'install.dismiss': 'Sluiten',
    'install.iosHint': 'Installeren: tik op Deel en dan “Zet op beginscherm”',

    'update.available': 'Er is een nieuwe versie beschikbaar',
    'update.action': 'Vernieuwen',

    'toolbar.switch': 'Wisselen',
    'toolbar.switchAria': 'Camera wisselen',
    'toolbar.screen': 'Scherm',
    'toolbar.screenAria': 'Scherm dimmen om batterij te sparen',
    'toolbar.addParent': 'Ouder toevoegen',
    'toolbar.addParentAria': 'Nog een ouder toevoegen',
    'toolbar.melody': 'Melodie',
    'toolbar.melodyAria': 'Een rustgevend melodietje voor je baby afspelen',
    'melody.playing': 'Slaapliedje speelt',
    'melody.alertsPaused': 'Huilmeldingen gepauzeerd',
    'battery.low': 'Batterij babyfoon bijna leeg',
    'battery.level': '{level}% over',
    'toolbar.sound': 'Geluid',
    'toolbar.soundAria': 'Geluid aan- of uitzetten',
    'toolbar.talk': 'Praten',
    'toolbar.talkAria': 'Ingedrukt houden om te praten',
    'toolbar.alerts': 'Meldingen',
    'toolbar.alertsAria': 'Geluidsmeldingen als de app gesloten is',
    'toolbar.invite': 'Uitnodigen',
    'toolbar.inviteAria': 'Nog een ouder uitnodigen',
    'toolbar.fill': 'Vullen',
    'toolbar.fit': 'Passend',
    'toolbar.frameAria': 'Beeld passend maken of scherm vullen',

    'safety.title': 'Privé & veilig van opzet',
    'safety.item1': 'Video en audio gaan <strong>rechtstreeks</strong> tussen je twee apparaten: peer-to-peer, end-to-end versleuteld (WebRTC / DTLS-SRTP).',
    'safety.item2': 'Geen account, geen registratie, niets wordt op een server bewaard. Er wordt nergens opgenomen.',
    'safety.item3': 'De verbindingslink is een willekeurige eenmalige id: alleen een apparaat dat je QR scant, kan meekijken.',
    'safety.author': 'Gemaakt door Harm Jan L. →',
    'safety.melodyCredit': 'Melodie van <a href="https://pixabay.com/users/alex-morgan/" target="_blank" rel="noopener noreferrer">Alex Morgan</a> op <a href="https://pixabay.com/" target="_blank" rel="noopener noreferrer">Pixabay</a>',

    'invite.title': 'Nog een ouder uitnodigen',
    'invite.sub': 'Laat iemand anders ook meekijken: scan deze code of open de link.',

    'videoOff.status': 'Streamen, scherm gedimd om batterij te sparen',
    'videoOff.hint': 'Tik ergens om het beeld te tonen',

    'error.camera.denied': 'Geef in je browserinstellingen toegang tot camera en microfoon en probeer opnieuw.',
    'error.camera.busy': 'De camera is in gebruik door een andere app. Sluit die en probeer opnieuw.',
    'error.camera.notFound': 'Geen camera gevonden. Sluit er een aan en probeer opnieuw.',
    'error.camera.generic': 'Kan de camera niet starten. Probeer het opnieuw.',
    'error.connection': 'Verbinding mislukt. Controleer je internet en probeer opnieuw.',

    'alerts.enabled': 'Meldingen aan. Je krijgt bericht, ook als de app gesloten is.',
    'alerts.disabled': 'Meldingen uit.',
    'alerts.denied': 'Meldingen zijn geblokkeerd. Sta ze toe in je browserinstellingen.',
    'alerts.unsupported': 'Deze browser ondersteunt geen achtergrondmeldingen.',
    'alerts.unsupportedIos': 'Zet de app op je beginscherm om meldingen te krijgen.',
    'alerts.failed': 'Meldingen konden niet worden ingeschakeld. Controleer je verbinding en probeer opnieuw.',

    'trust.defaultName': 'Ouder-{suffix}',
    'trust.newParent': 'Een nieuwe ouder heeft verbinding gemaakt',
    'trust.newParentNamed': '{name} heeft verbinding gemaakt',
    'trust.allow': 'Altijd toestaan',
    'trust.ignore': 'Negeren',
    'trust.manage': 'Vertrouwde ouders',
    'trust.title': 'Vertrouwde ouders',
    'trust.sub': 'Apparaten die deze babyfoon herkent, maken verbinding zonder melding. Verwijder ouders die je niet meer vertrouwt.',
    'trust.empty': 'Nog geen vertrouwde ouders.',
    'trust.remove': 'Verwijderen',
    'trust.nameAria': 'Naam van ouder',
  },
};

function primarySubtag(tag) {
  return String(tag || '').split('-')[0].toLowerCase();
}

// Locale baked into the URL by the build: /de/, /nl/, ... (English is the root).
// Takes priority over the browser's own languages because these paths are what
// search engines index and what people share — a visitor who follows a German
// result has to get the German page, or the rendered content stops matching the
// page's declared lang/hreflang.
function pathLocale() {
  try {
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    const sub = primarySubtag(seg);
    if (sub && SUPPORTED.includes(sub)) return sub;
  } catch (e) {
    /* ignore */
  }
  return null;
}

function detectLocale() {
  // Explicit override for testing, e.g. ?lang=de.
  try {
    const override = new URLSearchParams(window.location.search).get('lang');
    if (override && SUPPORTED.includes(primarySubtag(override))) return primarySubtag(override);
  } catch (e) {
    /* ignore malformed query */
  }
  const fromPath = pathLocale();
  if (fromPath) return fromPath;
  const candidates = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language];
  for (const cand of candidates) {
    const sub = primarySubtag(cand);
    if (SUPPORTED.includes(sub)) return sub;
  }
  return DEFAULT_LOCALE;
}

let current = DEFAULT_LOCALE;

export function getLocale() {
  return current;
}

function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (m, key) => (key in params ? String(params[key]) : m));
}

// Translate a key, interpolating {placeholders} from params. Falls back to the
// English string, then to the raw key, so a missing translation is never blank.
export function t(key, params) {
  const dict = messages[current] || messages.en;
  const str = dict[key] != null ? dict[key] : (messages.en[key] != null ? messages.en[key] : key);
  return interpolate(str, params);
}

// Plural-aware translate. Looks up `${baseKey}.<category>` using the locale's
// CLDR plural rules, formats {count} for the locale, and interpolates.
export function tCount(baseKey, count, params) {
  let category = 'other';
  try {
    category = new Intl.PluralRules(current).select(count);
  } catch (e) {
    /* fall back to 'other' */
  }
  const dict = messages[current] || messages.en;
  const key = dict[`${baseKey}.${category}`] != null ? `${baseKey}.${category}` : `${baseKey}.other`;
  let formatted = String(count);
  try {
    formatted = new Intl.NumberFormat(current).format(count);
  } catch (e) {
    /* keep plain number */
  }
  return t(key, { count: formatted, ...params });
}

// Attribute -> DOM property/attribute setters used by applyDom().
const ATTR_TARGETS = [
  ['data-i18n', (el, val) => { el.textContent = val; }],
  ['data-i18n-html', (el, val) => { el.innerHTML = val; }],
  ['data-i18n-aria-label', (el, val) => el.setAttribute('aria-label', val)],
  ['data-i18n-title', (el, val) => el.setAttribute('title', val)],
  ['data-i18n-placeholder', (el, val) => el.setAttribute('placeholder', val)],
];

// Fill every tagged element under `root`. Only our own trusted translation
// strings are ever assigned (data-i18n-html included), never user input.
export function applyDom(root = document) {
  ATTR_TARGETS.forEach(([attr, set]) => {
    root.querySelectorAll(`[${attr}]`).forEach((el) => {
      const key = el.getAttribute(attr);
      if (key) set(el, t(key));
    });
  });
}

export function initI18n() {
  current = detectLocale();
  try {
    document.documentElement.lang = current;
  } catch (e) {
    /* ignore */
  }
  applyDom(document);
  return current;
}
